import React from "react";

/**
 * How much shorter than the layout viewport the visible one has to be before
 * we call it a keyboard.
 *
 * A phone's collapsing browser chrome moves the visible viewport by ~60-100px;
 * a software keyboard takes 250-350px. 120 sits in the gap with room on both
 * sides. It exists for one decision only — whether the composer still owes the
 * home indicator its `env(safe-area-inset-bottom)` — and both ways of being
 * wrong there are a few pixels of padding, never a hidden control.
 */
const KEYBOARD_THRESHOLD_PX = 120;

/** What counts as a field: the thing a keyboard opens for. */
const EDITABLE = "input, textarea, [contenteditable]";

/**
 * Whether a keyboard shell is on screen right now (T20-M2).
 *
 * The floating layers the app mounts once, outside the router — the e-mail
 * verification banner, the push invitation — are `position: fixed` at the
 * bottom of the viewport with a z-index at or above the shell's, so while a
 * conversation IS the screen they sit on its composer. They ask this instead
 * of guessing from the route: the shells are exactly the screens that call
 * this hook with `active`, and Chat is one only below 768px.
 *
 * A counter and not a flag, so two screens overlapping during a route
 * transition cannot leave it false while one of them is still mounted.
 */
let shellsMounted = 0;
const shellListeners = new Set();
const notifyShells = () => shellListeners.forEach((listener) => listener());
const subscribeShells = (listener) => {
  shellListeners.add(listener);
  return () => shellListeners.delete(listener);
};

export function useKeyboardShellMounted() {
  return React.useSyncExternalStore(
    subscribeShells, () => shellsMounted > 0, () => false);
}

/**
 * Make a full-screen conversation exactly as tall as the part of the screen you
 * can see, hold the document still underneath it, and put the transcript back
 * where the person left it when that height moves.
 *
 * DY2.3 first, and T17-M1 is the change of pattern. What came before
 * COMPENSATED for the keyboard: the shell stayed in the page's flow, inside the
 * dashboard's chrome, and subtracted a hard-coded 5.5rem so the sum would come
 * out right. That works until the browser itself decides to move something —
 * which is precisely what iOS does when a field takes focus, and what Jorge saw
 * on a real phone: tap the composer, the page scrolls, the field is gone.
 *
 * The messaging pattern instead — WhatsApp's, Messenger's, every one of them —
 * is to be DIMENSIONED by the keyboard. The shell is taken out of the document
 * (`position: fixed`), sized to `--visual-viewport-height`, and the document
 * underneath is locked so that the browser's auto-scroll-on-focus has nothing
 * left to move. The composer then sits above the keyboard by construction,
 * because the box it is the last child of ends where the keyboard begins.
 *
 * Six things travel with that, and this hook owns all six.
 *
 * **1. The height.** `window.visualViewport` is the one API that reports the
 * visible rectangle — `100dvh` does NOT shrink for a keyboard on iOS — so the
 * shell's height is driven from it and `100dvh` stays as the fallback for
 * anything without the API. Written as a CSS variable rather than an inline
 * style so the declaration order in `index.css` decides, and so nothing
 * re-renders React on a keyboard animation frame.
 *
 * **2. The offset.** iOS does not only resize the visual viewport, it PANS it:
 * `visualViewport.offsetTop` goes positive while the keyboard is up. A
 * `position: fixed` box is fixed to the LAYOUT viewport, so it stays behind
 * that pan and its header rides off the top. `--visual-viewport-offset-top` is
 * what `.chat-shell` and `.dany-shell` translate by to come back.
 *
 * **3. The lock.** `overflow: hidden` on html and body, and `position: fixed`
 * on body, while the shell is mounted — on every screen, unconditionally.
 *
 * It used to be conditional on there being a document to move, measured at
 * mount, and T17 already found that condition saying yes on both screens for
 * an accidental reason (the tab bar's reservation still on the `body` when
 * this effect runs). T20-M1 took `.dany-shell` out of the document, which
 * leaves that document empty — and the measurement would have flipped to no
 * on the Dany thread, handing it a weaker lock than the chat's exactly when it
 * was being given the chat's pattern. That is an exemption by reasoning, which
 * is what T20 retired. Unconditional costs nothing: where the document cannot
 * scroll `scrollY` is 0, so the fixed body sits exactly where it was and
 * giving the position back is a scroll to where the page already is.
 *
 * The scroll position is saved and given back, or leaving the conversation
 * would drop the reader at the top of the list they came from. It does not
 * fight `components/layout/ScrollToTop.jsx`, and the two divide cleanly: that
 * one runs on a PATHNAME change and its effect fires after this cleanup, so it
 * wins where it should (leaving /dany for another page). Going back from a
 * conversation to the message list is a query-string change on the same path,
 * which it deliberately ignores — and that is exactly the case where the
 * reader wants to land where they were.
 *
 * **4. The re-anchor** — T16 M4, and it survives the change of pattern because
 * it was never about the document. Reported on a real iPhone, 2026-09-23: tap
 * the message field, the field goes out of view, and it comes back the moment
 * the first letter is typed. Re-anchoring the TRANSCRIPT was something only a
 * React render did — `Chat` scrolls its scroller in an effect, `DanyThread`
 * scrolls its `bottomRef` on `[turns, ...]` — and taking focus sets no state,
 * so between the tap and the first keystroke nothing ran. The lock above stops
 * the browser moving the page; it does not scroll a transcript, and nothing
 * else will. Both triggers stay and neither covers the other: `resize` is the
 * keyboard opening or closing, and `focusin` is focus moving between fields
 * while it is already open, which resizes nothing — the Dany thread's staged
 * cards are made of such fields. Not on `scroll`: that fires while the person
 * is panning the transcript themselves, and re-anchoring there would drag them
 * back to the bottom every time they tried to read upwards.
 *
 * **5. The focused field stays on screen** (T20-M1). Re-anchoring means "the
 * transcript to its bottom", which is right for the composer and wrong for a
 * field INSIDE the transcript: tap the first field of a staged card taller
 * than what is left above the keyboard, and the re-anchor scrolls it out from
 * under the finger that just tapped it. So after the screen's own re-anchor
 * the focused field is brought back with `block: "nearest"` — a no-op for the
 * composer, which is already in view, and the minimum scroll for a card. The
 * WRAPPING `<label>` when there is one, which is `DanyStageField`'s rule: a
 * field scrolled in without its label above it is a question nobody can read.
 * A card stage that takes focus by itself (`autoFocus`) also scrolls its own
 * wrapper one frame later, which lands after this and agrees with it.
 *
 * **6. The announcement** (T20-M2). While active, the hook counts itself in
 * `useKeyboardShellMounted`, which is how the app's floating bottom layers
 * know to step off the composer — see the store above.
 *
 * `active` is for a page that is only sometimes a full-screen shell — `Chat` is
 * the conversation on a phone and a two-pane split everywhere else. The
 * callback is read through a ref so a screen may pass an inline arrow without
 * re-subscribing the listeners on every render.
 */
export default function useVisualViewport(active = true, reanchor) {
  const reanchorRef = React.useRef(reanchor);
  reanchorRef.current = reanchor;

  React.useEffect(() => {
    if (!active) return undefined;

    const root = document.documentElement;
    const body = document.body;

    // Announced first and withdrawn in `unlock`, which both exits run.
    shellsMounted += 1;
    notifyShells();

    // ---- the lock ------------------------------------------------------
    // Read BEFORE anything is changed: once the body is fixed, the window has
    // nowhere to scroll and reports 0.
    const scrollY = window.scrollY;
    const saved = {
      htmlOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };

    root.style.overflow = "hidden";
    body.style.overflow = "hidden";
    // `overflow: hidden` alone does not stop iOS scrolling a document that has
    // somewhere to go. Taking the body out of flow does, and the top offset is
    // what keeps the page looking unmoved underneath. Unconditional — see the
    // docstring, point 3.
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    const unlock = () => {
      root.style.overflow = saved.htmlOverflow;
      body.style.overflow = saved.bodyOverflow;
      body.style.position = saved.bodyPosition;
      body.style.top = saved.bodyTop;
      body.style.left = saved.bodyLeft;
      body.style.right = saved.bodyRight;
      body.style.width = saved.bodyWidth;
      window.scrollTo(0, scrollY);
      shellsMounted -= 1;
      notifyShells();
    };

    // ---- the measurements ----------------------------------------------
    const vv = window.visualViewport;
    if (!vv) return unlock;                // 100dvh keeps its job

    const apply = () => {
      // Rounded down: a fractional height leaves a hairline of page background
      // under the composer on every scroll frame.
      root.style.setProperty("--visual-viewport-height",
                             `${Math.floor(vv.height)}px`);
      root.style.setProperty("--visual-viewport-offset-top",
                             `${Math.round(vv.offsetTop)}px`);
      // Read by `index.css` to take the home indicator's inset back off the
      // composer while the keyboard is covering it — see `.chat-shell
      // .composer-safe` and `.dany-shell .composer-safe`. Two cushions stacked
      // is a gap under the field.
      root.dataset.keyboard =
        vv.height < window.innerHeight - KEYBOARD_THRESHOLD_PX ? "open" : "closed";
    };

    // The focused field, back on screen after the re-anchor moved the
    // transcript — point 5 of the docstring. Nearest, never smooth: a keyboard
    // is animating and a second animation lands the card where neither meant.
    const keepFocusedInView = () => {
      const el = document.activeElement;
      if (!el || !el.matches?.(EDITABLE)) return;
      (el.closest("label") || el).scrollIntoView({
        behavior: "auto", block: "nearest",
      });
    };

    // The height first, then the screen, then the field. In that order on
    // purpose: the shell has to be its new size before anything asks where the
    // bottom of it is, or the scroll lands on the layout the keyboard has
    // already invalidated; and the field goes last because the re-anchor is
    // what may have moved it.
    const settle = () => {
      apply();
      reanchorRef.current?.();
      keepFocusedInView();
    };

    // iOS reports the new height across the keyboard's animation rather than
    // at the end of it, so the last frame is the one worth re-anchoring to.
    // A frame is also what lets the browser finish the reflow `apply` just
    // asked for before the scroll is computed.
    let frame = 0;
    const onResize = () => {
      apply();
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(settle);
    };

    // Focus moving between fields while the keyboard is already open, which
    // changes no height and so fires no resize. Guarded on the target being an
    // editable field: `focusin` also fires for every button on the screen, and
    // tapping Send is not a request to be scrolled anywhere.
    const onFocusIn = (event) => {
      const el = event.target;
      if (!el || !el.matches?.(EDITABLE)) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(settle);
    };

    apply();
    vv.addEventListener("resize", onResize);
    // `scroll` too: iOS fires it, without a resize, when it pans the visual
    // viewport. That pan is `offsetTop`, which the shell translates by.
    // Measurements only — see the docstring for why this one must not
    // re-anchor.
    vv.addEventListener("scroll", apply);
    window.addEventListener("focusin", onFocusIn);
    return () => {
      window.cancelAnimationFrame(frame);
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", apply);
      window.removeEventListener("focusin", onFocusIn);
      // Left set, these would pin every other page to the geometry the
      // keyboard happened to leave behind on the way out of this one.
      root.style.removeProperty("--visual-viewport-height");
      root.style.removeProperty("--visual-viewport-offset-top");
      delete root.dataset.keyboard;
      unlock();
    };
  }, [active]);
}
